

import React, { useState, useEffect } from 'react';
import imageCompression from 'browser-image-compression';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButtonMui from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { Box, Typography, Button, Card, CardContent, TextField, Tooltip, IconButton, Tabs, Tab, CircularProgress, Switch, FormControlLabel } from '@mui/material';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { useSelector, useDispatch } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import { FONT_FAMILY, MAIN_COLOR } from '../common/sharedFunctions';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import LinkIcon from '@mui/icons-material/Link';
import DeleteIcon from '@mui/icons-material/Delete';


export default function UploadImage() {
  const { t } = useTranslation();
  const settingsdata = useSelector(state => state.settingsdata);
  const dispatch = useDispatch();
  const { editSettings } = api;
  const [data, setData] = useState({ customerImageLinks: [], driverImageLinks: [] });
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const [uploadingIdx, setUploadingIdx] = useState(null);
  const [uploadingType, setUploadingType] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImg, setPreviewImg] = useState(null);

  useEffect(() => {
    if (settingsdata.settings) {
      setData({
        customerImageLinks: settingsdata.settings.customerImageLinks || Array(16).fill({ imageUrl: '', linkUrl: '' }),
        driverImageLinks: settingsdata.settings.driverImageLinks || Array(16).fill({ imageUrl: '', linkUrl: '' })
      });
    }
  }, [settingsdata.settings]);

  const handleImageUpload = async (file, idx, type) => {
    setUploadingIdx(idx);
    setUploadingType(type);
    // Compress/resize image before upload
    const options = {
      maxSizeMB: 0.3, // target max size 300KB
      maxWidthOrHeight: 900, // resize to max 900px
      useWebWorker: true
    };
    let compressedFile = file;
    try {
      compressedFile = await imageCompression(file, options);
    } catch (err) {
      // fallback to original file if compression fails
    }
    const storage = getStorage();
    const imgRef = storageRef(storage, `global/${type}Images/${idx}`);
    await uploadBytes(imgRef, compressedFile);
    const url = await getDownloadURL(imgRef);
    const arr = data[`${type}ImageLinks`] ? [...data[`${type}ImageLinks`]] : Array(16).fill({ imageUrl: '', linkUrl: '', enabled: true });
    arr[idx] = { ...arr[idx], imageUrl: url };
    setData({ ...data, [`${type}ImageLinks`]: arr });
    setUploadingIdx(null);
    setUploadingType(null);
  };

  const handleLinkChange = (e, idx, type) => {
    const arr = data[`${type}ImageLinks`] ? [...data[`${type}ImageLinks`]] : Array(16).fill({ imageUrl: '', linkUrl: '', enabled: true });
    arr[idx] = { ...arr[idx], linkUrl: e.target.value };
    setData({ ...data, [`${type}ImageLinks`]: arr });
  };

  const handleSwitchChange = (e, idx, type) => {
    const arr = data[`${type}ImageLinks`] ? [...data[`${type}ImageLinks`]] : Array(16).fill({ imageUrl: '', linkUrl: '', enabled: true });
    arr[idx] = { ...arr[idx], enabled: e.target.checked };
    setData({ ...data, [`${type}ImageLinks`]: arr });
  };

  const handleDelete = async (idx, type) => {
    const arr = data[`${type}ImageLinks`] ? [...data[`${type}ImageLinks`]] : Array(16).fill({ imageUrl: '', linkUrl: '', enabled: true });
    // Remove from Firebase Storage if imageUrl exists
    if (arr[idx] && arr[idx].imageUrl) {
      try {
        const storage = getStorage();
        const imgRef = storageRef(storage, `global/${type}Images/${idx}`);
        await deleteObject(imgRef);
      } catch (error) {
        // Optionally handle error (e.g., file not found)
        // console.error('Error deleting image from storage:', error);
      }
    }
    arr[idx] = { imageUrl: '', linkUrl: '', enabled: true };
    setData({ ...data, [`${type}ImageLinks`]: arr });
  };

  const handleSave = () => {
    setLoading(true);
    dispatch(editSettings({
      ...settingsdata.settings,
      customerImageLinks: data.customerImageLinks,
      driverImageLinks: data.driverImageLinks
    }));
    setTimeout(() => setLoading(false), 1000);
  };

  const renderImageGrid = (type) => (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr' },
      gap: 3,
    }}>
      {[...Array(16)].map((_, idx) => {
        const item = data[`${type}ImageLinks`]?.[idx] || { imageUrl: '', linkUrl: '', enabled: true };
        return (
          <Card key={idx} sx={{ display: 'flex', alignItems: 'center', borderRadius: 3, boxShadow: 2, p: 1, minHeight: 140, position: 'relative', background: '#fafbfc' }}>
            <Box sx={{ width: 90, height: 90, borderRadius: 2, overflow: 'hidden', bgcolor: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', mr: 2, border: '1px solid #e0e0e0', position: 'relative', cursor: 'pointer' }}>
              <input
                type="file"
                accept="image/*"
                hidden
                id={`upload-image-${type}-${idx}`}
                onChange={e => {
                  if (e.target.files && e.target.files[0]) {
                    handleImageUpload(e.target.files[0], idx, type);
                  }
                }}
              />
              <label htmlFor={`upload-image-${type}-${idx}`} style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', position: 'absolute', top: 0, left: 0 }}>
                {item.imageUrl ? (
                  <img
                    src={item.imageUrl}
                    alt="Preview"
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                    onClick={e => {
                      e.preventDefault();
                      setPreviewImg(item.imageUrl);
                      setPreviewOpen(true);
                    }}
                  />
                ) : (
                  <CloudUploadIcon style={{ color: '#bdbdbd', fontSize: 40 }} />
                )}
                {uploadingIdx === idx && uploadingType === type && (
                  <CircularProgress size={36} sx={{ position: 'absolute', top: 27, left: 27, zIndex: 2 }} />
                )}
              </label>
            </Box>
            <CardContent sx={{ flex: 1, p: 0, pr: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Tooltip title={item.imageUrl ? 'Change Image' : 'Upload Image'}>
                  <IconButton component="label" color="primary" sx={{ mr: 1 }}>
                    <CloudUploadIcon />
                    <input
                      type="file"
                      accept="image/*"
                      hidden
                      onChange={e => {
                        if (e.target.files && e.target.files[0]) {
                          handleImageUpload(e.target.files[0], idx, type);
                        }
                      }}
                    />
                  </IconButton>
                </Tooltip>
                {item.imageUrl && (
                  <Tooltip title="Remove Image & Link">
                    <IconButton color="error" onClick={() => handleDelete(idx, type)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <FormControlLabel
                  control={
                    <Switch
                      checked={item.enabled !== false}
                      onChange={e => handleSwitchChange(e, idx, type)}
                      color="primary"
                    />
                  }
                  label={item.enabled === false ? (t('hidden') || 'Hidden') : (t('visible') || 'Visible')}
                  sx={{ ml: 1 }}
                />
              </Box>
              <TextField
                label={t('link_url') + ' ' + (idx + 1)}
                value={item.linkUrl || ''}
                onChange={e => handleLinkChange(e, idx, type)}
                fullWidth
                size="small"
                sx={{ mt: 1 }}
                InputProps={{
                  startAdornment: <LinkIcon sx={{ color: MAIN_COLOR, mr: 1 }} />,
                }}
                placeholder="https://example.com"
              />
            </CardContent>
          </Card>
        );
      })}
    </Box>
  );

  return (
    <>
      <Box sx={{ maxWidth: 2000, mx: 'auto', my: 5, bgcolor: '#fff', borderRadius: 4, boxShadow: 4, p: { xs: 2, sm: 4 } }}>
        <Typography variant="h4" sx={{ fontFamily: FONT_FAMILY, color: MAIN_COLOR, mb: 3, textAlign: 'center', letterSpacing: 1, fontWeight: 700 }}>
          {t('upload_images_and_links') || 'Upload Images & Links'}
        </Typography>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          centered
          sx={{ mb: 4, '& .MuiTabs-indicator': { backgroundColor: MAIN_COLOR } }}
        >
          <Tab label={t('customer_images') || 'Customer Images'} sx={{ fontWeight: 600, color: tab === 0 ? MAIN_COLOR : '#888' }} />
          <Tab label={t('driver_images') || 'Driver Images'} sx={{ fontWeight: 600, color: tab === 1 ? MAIN_COLOR : '#888' }} />
        </Tabs>
        <Box sx={{ minHeight: 700 }}>
          {tab === 0 && renderImageGrid('customer')}
          {tab === 1 && renderImageGrid('driver')}
        </Box>
        <Box sx={{ textAlign: 'center', mt: 5 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSave}
            disabled={loading}
            sx={{ minWidth: 200, fontSize: 20, fontWeight: 700, borderRadius: 3, py: 1.5, px: 5, boxShadow: 2 }}
          >
            {loading ? (
              <><CircularProgress size={24} sx={{ color: '#fff', mr: 2 }} />{t('saving') || 'Saving...'}</>
            ) : (
              t('save') || 'Save'
            )}
          </Button>
        </Box>
      </Box>
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
        <DialogContent sx={{ p: 0, position: 'relative', background: '#222' }}>
          <IconButtonMui onClick={() => setPreviewOpen(false)} sx={{ position: 'absolute', top: 8, right: 8, color: '#fff', zIndex: 2 }}>
            <CloseIcon />
          </IconButtonMui>
          {previewImg && (
            <img src={previewImg} alt="Preview" style={{ width: '100%', maxHeight: '80vh', objectFit: 'contain', display: 'block', margin: '0 auto', background: '#222' }} />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
